// ─────────────────────────────────────────────
// KLOTHIQ — Reusable Export Actions Toolbar / Dropdown
// ─────────────────────────────────────────────
// Supports Excel (.xlsx) exports, PDF (.pdf) reports, receipt preview,
// and direct print options with accessible loading states and toast notifications.

import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { permissionForPath } from '@klothiq/domain';
import { useAuth } from '@/contexts/AuthContext';
import { Download, FileSpreadsheet, FileText, Printer, Eye, Loader2 } from 'lucide-react';
import {
  exportToExcel,
  exportToPdf,
  ExcelExportOptions,
  PdfExportOptions,
} from '@/utils/exportUtils';
import {
  exportProductsPdf,
  exportInventoryPdf,
  exportSalesPdf,
  exportPurchasesPdf,
  exportSaleReceiptPdf,
  ProductRow,
  InventoryRow,
  SaleRow,
  PurchaseRow,
  SaleWithItems,
} from '@/utils/pdfTemplates';
import { toast } from 'sonner';
import { usePrintPrivacy } from '@/hooks/usePrintPrivacy';
import { receiptWithPhonePrivacy } from '@/utils/customerPrintPrivacy';

export interface ExportActionsProps {
  excelOptions?: () => ExcelExportOptions | Promise<ExcelExportOptions>;
  pdfOptions?: () => PdfExportOptions | Promise<PdfExportOptions>;
  onViewReceipt?: () => void;
  onPrint?: () => void;
  disabled?: boolean;
  className?: string;
  variant?: 'buttons' | 'dropdown';
  exportType?: 'products' | 'inventory' | 'sales' | 'purchases' | 'saleReceipt';
  data?: Record<string, unknown>[];
  sale?: SaleWithItems;
  orgName?: string;
}

export default function ExportActions({
  excelOptions,
  pdfOptions,
  onViewReceipt,
  onPrint,
  disabled = false,
  className = '',
  variant = 'buttons',
  exportType,
  data,
  sale,
  orgName = 'Klothiq',
}: ExportActionsProps) {
  const [exportingType, setExportingType] = useState<'excel' | 'pdf' | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { can } = useAuth();
  const { pathname } = useLocation();
  const permission = permissionForPath(pathname);
  const exportAllowed =
    can('financials.view') &&
    (!permission?.startsWith('report_') || can(permission.replace(/\.view$/, '.export')));
  const { askPrivacy, privacyDialog } = usePrintPrivacy(exportAllowed);
  if (!exportAllowed) return null;

  const handleExcelExport = async () => {
    if (!excelOptions) return;
    setExportingType('excel');
    try {
      const opts = await excelOptions();
      exportToExcel(opts);
      toast.success('Excel report downloaded successfully');
    } catch (err: unknown) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Failed to export Excel report');
    } finally {
      setExportingType(null);
      setIsOpen(false);
    }
  };

  const handlePdfExport = async () => {
    if (exportType) {
      setExportingType('pdf');
      try {
        const rows = data ?? [];
        switch (exportType) {
          case 'products':
            exportProductsPdf(rows as unknown as ProductRow[], orgName);
            break;
          case 'inventory':
            exportInventoryPdf(rows as unknown as InventoryRow[], orgName);
            break;
          case 'sales':
            exportSalesPdf(rows as unknown as SaleRow[], orgName);
            break;
          case 'purchases':
            exportPurchasesPdf(rows as unknown as PurchaseRow[], orgName);
            break;
          case 'saleReceipt':
            if (sale) {
              const masked = await askPrivacy();
              if (masked === null) return;
              exportSaleReceiptPdf(receiptWithPhonePrivacy(sale, masked), orgName);
            }
            break;
        }
        toast.success('PDF document downloaded successfully');
      } catch (err: unknown) {
        console.error(err);
        toast.error(err instanceof Error ? err.message : 'Failed to export PDF document');
      } finally {
        setExportingType(null);
        setIsOpen(false);
      }
      return;
    }

    if (!pdfOptions) return;
    setExportingType('pdf');
    try {
      const opts = await pdfOptions();
      exportToPdf(opts);
      toast.success('PDF document downloaded successfully');
    } catch (err: unknown) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Failed to export PDF document');
    } finally {
      setExportingType(null);
      setIsOpen(false);
    }
  };

  const handleViewReceiptClick = () => {
    if (onViewReceipt) {
      onViewReceipt();
      setIsOpen(false);
    }
  };

  const handlePrintClick = () => {
    if (onPrint) {
      onPrint();
      setIsOpen(false);
    }
  };

  if (variant === 'dropdown') {
    return (
      <>
        <div className={`relative inline-block text-left ${className}`}>
          <button
            type="button"
            disabled={disabled || exportingType !== null}
            onClick={() => setIsOpen(!isOpen)}
            aria-expanded={isOpen}
            aria-haspopup="true"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs font-medium text-foreground shadow-sm hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 transition-colors"
          >
            {exportingType ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            ) : (
              <Download className="h-3.5 w-3.5 text-muted-foreground" />
            )}
            <span>Export / Actions</span>
          </button>

          {isOpen && (
            <>
              <div
                className="fixed inset-0 z-20"
                onClick={() => setIsOpen(false)}
                data-field-guide-scope
              />
              <div className="absolute right-0 z-30 mt-1 w-48 rounded-md border border-border bg-card p-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                {onViewReceipt && (
                  <button
                    type="button"
                    onClick={handleViewReceiptClick}
                    disabled={exportingType !== null}
                    className="flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-xs text-foreground hover:bg-muted/80 disabled:opacity-50"
                  >
                    <Eye className="h-4 w-4 text-indigo-600" />
                    <span>Preview Receipt</span>
                  </button>
                )}

                {excelOptions && (
                  <button
                    type="button"
                    onClick={handleExcelExport}
                    disabled={exportingType !== null}
                    className="flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-xs text-foreground hover:bg-muted/80 disabled:opacity-50"
                  >
                    <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
                    <span>Export to Excel</span>
                  </button>
                )}

                {(pdfOptions || exportType) && (
                  <button
                    type="button"
                    onClick={handlePdfExport}
                    disabled={exportingType !== null}
                    className="flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-xs text-foreground hover:bg-muted/80 disabled:opacity-50"
                  >
                    <FileText className="h-4 w-4 text-rose-600" />
                    <span>Export to PDF</span>
                  </button>
                )}

                {onPrint && (
                  <button
                    type="button"
                    onClick={handlePrintClick}
                    disabled={exportingType !== null}
                    className="flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-xs text-foreground hover:bg-muted/80 disabled:opacity-50"
                  >
                    <Printer className="h-4 w-4 text-blue-600" />
                    <span>Print Document</span>
                  </button>
                )}
              </div>
            </>
          )}
        </div>
        {privacyDialog}
      </>
    );
  }

  return (
    <>
      <div className={`flex items-center gap-2 ${className}`}>
        {onViewReceipt && (
          <button
            type="button"
            disabled={disabled || exportingType !== null}
            onClick={handleViewReceiptClick}
            title="Preview Customer Receipt"
            aria-label="Preview Customer Receipt"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs font-medium text-foreground shadow-sm hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50"
          >
            <Eye className="h-3.5 w-3.5 text-indigo-600" />
            <span className="hidden sm:inline">Receipt</span>
          </button>
        )}

        {excelOptions && (
          <button
            type="button"
            disabled={disabled || exportingType !== null}
            onClick={handleExcelExport}
            title="Export to Excel (.xlsx)"
            aria-label="Export to Excel"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs font-medium text-foreground shadow-sm hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50"
          >
            {exportingType === 'excel' ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-emerald-600" />
            ) : (
              <FileSpreadsheet className="h-3.5 w-3.5 text-emerald-600" />
            )}
            <span className="hidden sm:inline">Excel</span>
          </button>
        )}

        {(pdfOptions || exportType) && (
          <button
            type="button"
            disabled={disabled || exportingType !== null}
            onClick={handlePdfExport}
            title="Export to PDF (.pdf)"
            aria-label="Export to PDF"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs font-medium text-foreground shadow-sm hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50"
          >
            {exportingType === 'pdf' ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-rose-600" />
            ) : (
              <FileText className="h-3.5 w-3.5 text-rose-600" />
            )}
            <span className="hidden sm:inline">PDF</span>
          </button>
        )}

        {onPrint && (
          <button
            type="button"
            disabled={disabled || exportingType !== null}
            onClick={handlePrintClick}
            title="Print document"
            aria-label="Print document"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs font-medium text-foreground shadow-sm hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50"
          >
            <Printer className="h-3.5 w-3.5 text-blue-600" />
            <span className="hidden sm:inline">Print</span>
          </button>
        )}
      </div>
      {privacyDialog}
    </>
  );
}
